// Dungeon catalogue: starter pools and pick functions for ADR 0030.
//
// Scope of this file (Step 10c-boon, first slice of ADR 0030):
//   - STARTER_BOON_POOL: 15 boons covering all 11 categories.
//   - boonSeed: deterministic, independent from `subtypeSeed` (10b-1) and
//     `battleSeed` (10b-2) so that re-rolling one channel never re-rolls
//     the others.
//   - pickBoon: even-weight roll with up-to-3 duplicate rerolls and a
//     fallback to the unowned subset. Returns null only when the pool is
//     fully owned — unreachable for the starter pool (15) + cap (3) but
//     pinned by tests as a defensive contract for future catalogue swaps.
//
// Text/treasure/ambush/boss-escort/midboss/rare entries from ADR 0030
// land in their own slices (10c-event / 10c-boss-replace / 10c-ambush).
import { Rng } from './rng.js';
// ---------------------------------------------------------------------------
// Boon starter pool (ADR 0030 §2.3.1, 15 entries, 11-category cover)
// ---------------------------------------------------------------------------
export const STARTER_BOON_POOL = [
    { id: 'boon.attack-up', category: 'stat-percent', name: '攻撃の手応え', value: 0.1 },
    { id: 'boon.defense-up', category: 'stat-percent', name: '守りの構え', value: 0.1 },
    { id: 'boon.hp-up', category: 'stat-percent', name: '命の脈動', value: 0.15 },
    { id: 'boon.crit-rate', category: 'crit', name: '集中の瞳', value: 0.05 },
    { id: 'boon.crit-damage', category: 'crit', name: '鋭利の極み', value: 0.2 },
    { id: 'boon.evasion', category: 'evasion', name: '風読み', value: 0.08 },
    { id: 'boon.damage-reduction', category: 'damage-reduction', name: '鉄壁の意志', value: 0.08 },
    { id: 'boon.fire-mastery', category: 'element', name: '焔の祝福', value: 0.15, elementTag: 'fire' },
    { id: 'boon.ice-mastery', category: 'element', name: '氷の祝福', value: 0.15, elementTag: 'ice' },
    { id: 'boon.regen', category: 'regen', name: '静かな再生', value: 0.2 },
    { id: 'boon.cooldown', category: 'cooldown', name: '時の歯車', value: -0.1 },
    { id: 'boon.summon-up', category: 'summon', name: '召喚契約', value: 0.2 },
    { id: 'boon.placeable-up', category: 'placeable', name: '建造熟達', value: 0.2 },
    { id: 'boon.gold-up', category: 'gold', name: '金運の風', value: 0.25 },
    { id: 'boon.xp-up', category: 'xp', name: '経験の刻', value: 0.25 },
];
// ---------------------------------------------------------------------------
// Seed derivation (independent from subtypeSeed and battleSeed)
// ---------------------------------------------------------------------------
/**
 * Deterministic boon-roll seed for a given camp/boon node.
 *
 * Constants chosen so the output is statistically independent of
 * `subtypeSeed` (Knuth `0x9e3779b9` / `0x85ebca6b`) and `battleSeed`
 * (Murmur3-derived `0xa3c59ac3` / `0x1b873593`). Tests pin that all
 * three functions return distinct values for matched inputs.
 */
export function boonSeed(mapSeed, coord) {
    const [floor, index] = coord;
    return ((mapSeed ^ Math.imul(floor, 0xc6a4a793) ^ Math.imul(index, 0xdaa1bf8d)) >>> 0);
}
/**
 * Pick one boon for the camp/boon submenu. Returns null only when the
 * caller's `activeBoons` already covers the entire pool — caller must
 * treat null as "no grant this entry". For the starter pool (15) and
 * FIFO cap of 3, the null branch is unreachable but pinned defensively.
 *
 * Roll procedure (ADR 0030 §2.3.3):
 *   1. seed Rng from `boonSeed(runSeed, coord)`
 *   2. roll an even-weight index into the pool
 *   3. if the picked id is already in `activeBoons`, reroll (up to 3 retries)
 *   4. on the 4th duplicate, sample uniformly from the unowned subset
 *   5. unowned subset empty → null
 */
export function pickBoon(opts) {
    const pool = opts.pool ?? STARTER_BOON_POOL;
    if (pool.length === 0)
        return null;
    const owned = new Set(opts.activeBoons.map((b) => b.id));
    const rng = Rng.fromSeed(boonSeed(opts.runSeed, opts.coord));
    for (let attempt = 0; attempt < 4; attempt++) {
        const pick = pool[rng.nextInt(pool.length)];
        if (!owned.has(pick.id)) {
            return toState(pick);
        }
    }
    // 4 duplicates in a row — fall back to the unowned subset.
    const unowned = pool.filter((p) => !owned.has(p.id));
    if (unowned.length === 0)
        return null;
    return toState(unowned[rng.nextInt(unowned.length)]);
}
function toState(def) {
    if (def.elementTag !== undefined) {
        return {
            id: def.id,
            category: def.category,
            value: def.value,
            elementTag: def.elementTag,
        };
    }
    return { id: def.id, category: def.category, value: def.value };
}
// ---------------------------------------------------------------------------
// Boss / midboss / rare template resolution (ADR 0030 §2.5)
// ---------------------------------------------------------------------------
/**
 * Starter Tier for each theme. ADR 0027 pins one authored Tier per theme
 * (forest=1, cave=2, temple=3, tower=4, abyss=5). High-Tier dungeons reuse
 * the same theme's starter id and only scale level via {@link bossLevelBoost}.
 *
 * Keyed by `string` (the theme part of `DungeonTemplate.id`) rather than the
 * content-owned `DungeonTheme` literal so shared has no reverse dependency on
 * content. Content's `templates.test.ts` pins these same numbers.
 */
export const STARTER_TIER_BY_THEME = {
    forest: 1,
    cave: 2,
    temple: 3,
    tower: 4,
    abyss: 5,
};
function themeOf(template) {
    const parts = template.id.split('.');
    if (parts.length !== 3 || parts[0] !== 'dungeon') {
        throw new Error(`themeOf: malformed template id '${template.id}'`);
    }
    return parts[1];
}
function starterTierOf(template) {
    const theme = themeOf(template);
    const tier = STARTER_TIER_BY_THEME[theme];
    if (tier === undefined) {
        throw new Error(`starterTierOf: unknown theme '${theme}'`);
    }
    return tier;
}
/**
 * Resolve the enemy template id used for the boss of `template`'s dungeon.
 * Always returns `enemy.boss.<theme>.t<starterTier>`; high-Tier dungeons reuse
 * the starter id and scale via {@link bossLevelBoost}.
 */
export function bossTemplateId(template) {
    // Tier-0 tutorial onramp (ADR 0038 §2.1) has its own gentle boss
    // (`enemy.boss.<theme>.t0`); every other tier reuses the theme's starter boss
    // and scales via {@link bossLevelBoost}.
    const tier = template.tier <= 0 ? 0 : starterTierOf(template);
    return `enemy.boss.${themeOf(template)}.t${tier}`;
}
/** As {@link bossTemplateId} but for midboss role (`elite` parent, midboss subtype + boss escorts). */
export function midbossTemplateId(template) {
    return `enemy.midboss.${themeOf(template)}.t${starterTierOf(template)}`;
}
/** As {@link bossTemplateId} but for rarepack's rare role. */
export function rareTemplateId(template) {
    return `enemy.rare.${themeOf(template)}.t${starterTierOf(template)}`;
}
// ---------------------------------------------------------------------------
// Boss level boost (ADR 0030 §2.5.3)
// ---------------------------------------------------------------------------
/**
 * Per-Tier additive boost applied to the boss's level on top of
 * `template.baseEnemyLevel`.
 *
 * W1-2 retune (ADR 0042, supersedes the ADR 0030 §2.5.3 table): a flat +5 at
 * low tiers put the T1 boss 50% above the recommended hero level (Lv15 vs
 * Lv10) — measured unwinnable at the recommended level (F2: 0/24 solo AND
 * party). Low-tier bands now keep the boss ≈10-15% above baseEnemyLevel:
 * Tier 1-2 → +2, 3-4 → +4, 5-9 → +8, 10-19 → +12, 20+ → +15.
 */
export function bossLevelBoost(tier) {
    if (tier <= 0)
        return 1; // Tier-0 tutorial onramp (ADR 0038 §2.1): gentle boss.
    if (tier <= 2)
        return 2;
    if (tier <= 4)
        return 4;
    if (tier <= 9)
        return 8;
    if (tier <= 19)
        return 12;
    return 15;
}
/** Tier-banded escort count distribution (ADR 0030 §2.5.4). */
export const ESCORT_DISTRIBUTION = [
    { tierMin: 1, tierMax: 4, weights: [0.8, 0.2, 0.0] },
    { tierMin: 5, tierMax: 9, weights: [0.4, 0.45, 0.15] },
    { tierMin: 10, tierMax: 19, weights: [0.2, 0.4, 0.4] },
    { tierMin: 20, tierMax: Infinity, weights: [0.1, 0.3, 0.6] },
];
/**
 * Deterministic seed for the boss-escort roll, independent from
 * `subtypeSeed` (10b-1), `battleSeed` (10b-2) and `boonSeed` (10c-boon).
 *
 * Constants are distinct PCG-like multipliers so the resulting stream cannot
 * collide with the other seed channels for the same `(mapSeed, coord)`.
 */
export function bossEscortSeed(mapSeed, coord) {
    const [floor, index] = coord;
    return ((mapSeed ^ Math.imul(floor, 0xb5297a4d) ^ Math.imul(index, 0x68e31da4)) >>> 0);
}
/**
 * Pick the boss escort lineup for a given boss node entry. Returns 0-2
 * midboss template ids (each one is the `enemy.midboss.<theme>.t<starterTier>`
 * for `template`). Deterministic for the same `(template.tier, runSeed, coord)`.
 *
 * Roll procedure (ADR 0030 §2.5.4):
 *   1. seed Rng from `bossEscortSeed(runSeed, coord)`
 *   2. draw one `nextFloat()` and bucket it into [0, 1, 2] via the Tier-banded weights
 *   3. emit that many copies of `midbossTemplateId(template)`
 */
export function pickBossEscorts(opts) {
    // Tier 0 (tutorial onramp, ADR 0038 §2.1) never spawns boss escorts: the
    // forest midboss escort (`enemy.midboss.forest.t1`, 1500 HP) is a party-scale
    // wall a solo fresh Lv1 hero cannot beat, which is exactly the seed-dependent
    // failure the onramp must not have. The tutorial boss fights alone.
    if (opts.template.tier <= 0)
        return [];
    const dist = ESCORT_DISTRIBUTION.find((d) => opts.template.tier >= d.tierMin && opts.template.tier <= d.tierMax);
    if (dist === undefined) {
        throw new Error(`pickBossEscorts: no escort distribution for tier ${opts.template.tier}`);
    }
    const rng = Rng.fromSeed(bossEscortSeed(opts.runSeed, opts.coord));
    const roll = rng.nextFloat();
    let count = 0;
    let acc = 0;
    for (let i = 0; i < dist.weights.length; i++) {
        acc += dist.weights[i];
        if (roll < acc) {
            count = i;
            break;
        }
    }
    const escortId = midbossTemplateId(opts.template);
    return Array.from({ length: count }, () => escortId);
}
//# sourceMappingURL=dungeon-catalogue.js.map